<template>
    <div class="voc-item-upload-link">
        <pendo-form
            ref="linkInput"
            v-model="isFormValid"
            class="voc-item-upload-link__form"
            :model="linkForm">
            <pendo-form-item
                :rules="urlValidationRule"
                data-cy="voc-attach-link-form-item"
                prop="urlInput">
                <pendo-input
                    v-model="linkForm.urlInput"
                    :disabled="isSaving"
                    data-cy="voc-attach-link-form-input"
                    @input="resetValidation"
                    @keydown.enter.self.prevent="submitLinkToForm" />
            </pendo-form-item>
        </pendo-form>
        <div class="voc-item-upload-link__actionIcons">
            <pendo-icon-button
                class="voc-item-upload-link__icon"
                icon="x"
                icon-size="16"
                :disabled="isSaving"
                @click="$emit('closeLinkInput')" />
            <pendo-icon-button
                class="voc-item-upload-link__icon"
                :stroke="uploadStroke"
                :disabled="isSaving"
                icon="check"
                icon-size="16"
                data-cy="voc-attach-link-form-confirm"
                @click="submitLinkToForm" />
        </div>
    </div>
</template>
<script>
import { PendoInput, PendoIconButton, PendoForm, PendoFormItem } from '@pendo/components';
import { getComputedColor } from '@utils/color';
import { mapActions, mapState } from 'vuex';
import { v4 as uuid } from 'uuid';
import { VALID_LINK_REGEX } from '@/common/regex/regex.utils';

export default {
    name: 'VocItemUploadLink',
    components: {
        PendoInput,
        PendoIconButton,
        PendoForm,
        PendoFormItem,
    },
    inject: ['vocItem'],
    props: {
        blade: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['closeLinkInput', 'validateError'],
    data () {
        return {
            uploadStroke: getComputedColor('--palette-teal-70'),
            linkForm: {
                urlInput: '',
            },
            isFormValid: true,
            isSaving: false,
            urlValidationRule: [
                {
                    required: true,
                    message: "Please add a valid URL starting with 'http://' or 'https://'",
                    pattern: VALID_LINK_REGEX,
                    trigger: ['submit'],
                },
                {
                    required: true,
                    message: 'Link URL cannot contain any whitespaces',
                    pattern: new RegExp('^\\S*$'),
                    trigger: ['submit'],
                },
                {
                    message: 'Link URL cannot be longer than 2000 characters',
                    max: 2000,
                    trigger: ['submit'],
                },
            ],
        };
    },
    computed: {
        ...mapState({
            localAttachmentList: (state) => state.vocItems.attachmentList,
        }),
    },
    methods: {
        ...mapActions({
            addToAttachmentList: 'vocItems/addToAttachmentList',
        }),
        resetValidation () {
            this.$refs.linkInput.resetValidation();
        },
        submitLinkToForm () {
            this.$refs.linkInput.validate(async (valid) => {
                if (valid) {
                    const linksArray = this.localAttachmentList.filter((attachment) => attachment.type === 'link');
                    if (linksArray.length < 10) {
                        try {
                            if (this.blade) {
                                this.isSaving = true;
                            }
                            await this.addToAttachmentList({
                                attachmentFile: {
                                    name: this.linkForm.urlInput,
                                    title: this.getTitleFromUrl(this.linkForm.urlInput),
                                },
                                type: 'link',
                                id: uuid(),
                                vocItem: this.vocItem,
                                persist: this.blade,
                            });
                        } catch {
                            this.$emit('validateError', 'There was an error attaching the link. Please try again');
                        } finally {
                            this.isSaving = false;
                        }
                    } else {
                        this.$emit('validateError', 'Could not upload this link, max 10 links allowed.');
                    }
                    this.linkForm.urlInput = '';
                    this.$emit('closeLinkInput');
                }
            });
        },
        getTitleFromUrl (url) {
            let domain = new URL(url).hostname;
            if (domain.startsWith('www.')) {
                domain = domain.slice(4);
            }
            const parts = domain.split('.');

            return parts[0];
        },
    },
};
</script>
<style lang="less" scoped>
.voc-item-upload-link {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;

    &__form {
        width: 100%;
    }
    &__actionIcons {
        display: flex;
        margin-left: 8px;
    }
    &__icon {
        border: 1px solid var(--palette-teal-70);
        color: var(--palette-teal-70);
        opacity: 0.8;
        &:hover {
            color: var(--palette-teal-70);
            border: 1px solid var(--palette-teal-70);
        }
    }
}
</style>
